import React, { memo, useCallback } from 'react';
import { ButtonSize, ButtonVariant } from "../button";
import { IconBin } from "../icons";
import { Text } from "../text";
import { getUniqueId } from "../utils";
import FileDetailsProgress from "./FileDetailsProgress";
import { FILE_DETAILS_BUTTON_SUB_ROW_MIN_WIDTH, FILE_DETAILS_BUTTON_SUB_ROW_MIN_WIDTH_SMALL_VIEWPORT, FILE_DETAILS_BUTTON_SUB_ROW_MIN_WIDTH_WITH_PROGRESS } from "./constants";
import { FileRow, FileSubRow, IconButton } from "./helperComponents";
import { formatBytes } from "./utils";
import { jsx as ___EmotionJSX } from "@emotion/core";

var getFileDetailsResponsiveStyles = function getFileDetailsResponsiveStyles(isInCardLayout, hasFileProgress) {
  if (isInCardLayout) {
    return {
      rowFlexDirection: 'column',
      rowAlignItems: 'flex-start',
      buttonSubRowJustifyContent: 'space-between',
      buttonSubRowMinWidth: FILE_DETAILS_BUTTON_SUB_ROW_MIN_WIDTH_SMALL_VIEWPORT
    };
  }

  return {
    rowFlexDirection: 'row',
    rowAlignItems: 'center',
    buttonSubRowJustifyContent: 'flex-end',
    buttonSubRowMinWidth: hasFileProgress ? FILE_DETAILS_BUTTON_SUB_ROW_MIN_WIDTH_WITH_PROGRESS : FILE_DETAILS_BUTTON_SUB_ROW_MIN_WIDTH
  };
};

var FileDetails = /*#__PURE__*/memo(function (_ref) {
  var removeFileButtonsRefMap = _ref.removeFileButtonsRefMap,
      cancelUploadButtonTitle = _ref.cancelUploadButtonTitle,
      deleteFileButtonTitle = _ref.deleteFileButtonTitle,
      file = _ref.file,
      files = _ref.files,
      getUploadProgressAriaLabel = _ref.getUploadProgressAriaLabel,
      getUploadProgressSummary = _ref.getUploadProgressSummary,
      handleFocus = _ref.handleFocus,
      isInCardLayout = _ref.isInCardLayout,
      index = _ref.index,
      onFileAttached = _ref.onFileAttached,
      requests = _ref.requests,
      renderFileDetailsIcon = _ref.renderFileDetailsIcon,
      renderFileDetailsLabel = _ref.renderFileDetailsLabel,
      setFiles = _ref.setFiles,
      uploadProgressAriaLive = _ref.uploadProgressAriaLive;
  var name = file.name,
      size = file.size;
  var fileSize = formatBytes(size);
  var key = "".concat(name).concat(size);
  var htmlSafeKey = encodeURIComponent(key);
  var id = getUniqueId("file-".concat(htmlSafeKey, "-").concat(index));
  var uploadProgress = requests.hasOwnProperty(key) ? requests[key].percentage : undefined;
  var isStillUploading = uploadProgress !== 100;
  var uploadSize = !!uploadProgress && isStillUploading ? "".concat(formatBytes(size * uploadProgress / 100), " / ").concat(fileSize) : fileSize;

  var handleRemoveFileButtonRef = function handleRemoveFileButtonRef(el) {
    if (removeFileButtonsRefMap && removeFileButtonsRefMap.current) {
      removeFileButtonsRefMap.current[index] = el;
    }
  };

  var handleRemoveFile = useCallback(function () {
    var newFiles = files.filter(function (file, fileIndex) {
      return fileIndex !== index;
    });
    onFileAttached(newFiles);
    setFiles(newFiles);

    if (requests.hasOwnProperty(key) && isStillUploading) {
      requests[key].abort();
    }

    handleFocus(index);
  }, [files, handleFocus, index, isStillUploading, key, onFileAttached, requests, setFiles]);
  var hasFileProgress = typeof uploadProgress === 'number' && isStillUploading;

  var _getFileDetailsRespon = getFileDetailsResponsiveStyles(isInCardLayout, hasFileProgress),
      rowFlexDirection = _getFileDetailsRespon.rowFlexDirection,
      rowAlignItems = _getFileDetailsRespon.rowAlignItems,
      buttonSubRowJustifyContent = _getFileDetailsRespon.buttonSubRowJustifyContent,
      buttonSubRowMinWidth = _getFileDetailsRespon.buttonSubRowMinWidth;

  return ___EmotionJSX(FileRow, {
    alignItems: rowAlignItems,
    flexDirection: rowFlexDirection,
    key: index
  }, ___EmotionJSX(FileSubRow, {
    id: id
  }, renderFileDetailsIcon(file), renderFileDetailsLabel(file)), ___EmotionJSX(FileSubRow, {
    minWidth: buttonSubRowMinWidth,
    justifyContent: buttonSubRowJustifyContent
  }, ___EmotionJSX(Text, {
    color: "neutral70"
  }, uploadSize), hasFileProgress ? ___EmotionJSX(FileDetailsProgress, {
    cancelUploadButtonTitle: cancelUploadButtonTitle,
    handleRemoveFile: handleRemoveFile,
    uploadProgress: uploadProgress,
    "aria-live": uploadProgressAriaLive,
    fileName: name,
    getAriaLabel: getUploadProgressAriaLabel,
    getProgressSummary: getUploadProgressSummary
  }) : ___EmotionJSX(IconButton, {
    "aria-describedby": id,
    baseColor: "red",
    icon: ___EmotionJSX(IconBin, {
      color: "red70",
      title: deleteFileButtonTitle
    }),
    onClick: handleRemoveFile,
    ref: handleRemoveFileButtonRef,
    size: ButtonSize.Small,
    variant: ButtonVariant.Tertiary
  })));
});
export default FileDetails;